import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { calcTotals, currencySymbol } from "@/pages/CreateInvoice";
import { Button } from "@/components/ui/button";
import { Download, Printer, Share2, QrCode, ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import axios from "axios";
import { trackEvent } from "@/lib/track";

export default function InvoicePreview() {
  const { invoice } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const paperRef = useRef(null);
  const [showQR, setShowQR] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState(null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/invoice`)
      .then((r) => setInvoiceSettings(r.data))
      .catch(() => setInvoiceSettings({}));
  }, []);

  const totals = calcTotals(invoice);
  const sym = currencySymbol(invoice.meta.currency);

  const handleDownload = async () => {
    toast.loading("Generating PDF...", { id: "pdf" });
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);
      const el = paperRef.current;
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, "PNG", 0, 0, w, h);
      pdf.save(`${invoice.meta.number || "invoice"}.pdf`);
      trackEvent("pdf_downloaded", { number: invoice.meta.number });
      toast.success("PDF downloaded", { id: "pdf" });
    } catch (e) {
      toast.error("Could not generate PDF", { id: "pdf" });
    }
  };

  useEffect(() => {
    if (location.state?.autoDownload) {
      const t = setTimeout(handleDownload, 500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Invoice ${invoice.meta.number}`, url });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Invoice link copied");
    }
  };

  const activePayments = Object.entries(invoice.payments).filter(([, v]) => v).map(([k]) => k);
  const qrPayload = `Invoice ${invoice.meta.number} · ${sym}${totals.total.toFixed(2)} · ${invoice.company.name || "Simple Invoice"}`;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 no-print">
        <div>
          <button data-testid="back-btn" onClick={() => navigate("/create")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft size={14} /> Back to editor
          </button>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Invoice Preview</h1>
          <p className="mt-1 text-sm text-muted-foreground">Review your invoice and download, print or share.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="download-pdf-btn" onClick={handleDownload} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"><Download size={14} className="mr-1" /> Download PDF</Button>
          <Button data-testid="print-btn" onClick={handlePrint} variant="outline" className="rounded-full"><Printer size={14} className="mr-1" /> Print</Button>
          <Button data-testid="share-btn" onClick={handleShare} variant="outline" className="rounded-full"><Share2 size={14} className="mr-1" /> Share</Button>
          <Button data-testid="qr-btn" onClick={() => setShowQR(!showQR)} variant="outline" className="rounded-full"><QrCode size={14} className="mr-1" /> {showQR ? "Hide" : "QR Code"}</Button>
        </div>
      </div>

      <div className="flex justify-center">
        <div id="invoice-print" ref={paperRef} className="w-full max-w-[820px] bg-white text-slate-900 rounded-2xl shadow-2xl border border-slate-200 p-10 sm:p-12" style={{ aspectRatio: "auto" }}>
          {/* Header */}
          <div className="flex items-start justify-between mb-10">
            <div className="flex items-start gap-4">
              {invoice.company.logo ? (
                <img src={invoice.company.logo} alt="logo" className="w-16 h-16 object-contain rounded-xl border border-slate-200" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-blue-600 text-white grid place-items-center font-heading font-bold text-xl">
                  {(invoice.company.name || "S").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <div className="font-heading font-bold text-lg">{invoice.company.name || "Your Company"}</div>
                <div className="text-xs text-slate-500 whitespace-pre-line mt-1">{invoice.company.address}</div>
                {invoice.company.email && <div className="text-xs text-slate-500 mt-1">Email: {invoice.company.email}</div>}
                {invoice.company.phone && <div className="text-xs text-slate-500">Phone: {invoice.company.phone}</div>}
                {invoice.company.gst && <div className="text-xs text-slate-500">GST: {invoice.company.gst}</div>}
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading font-bold text-3xl tracking-tight">INVOICE</div>
              <div className="mt-3 text-xs space-y-1">
                <div><span className="text-slate-500">Invoice No: </span><span className="font-medium">{invoice.meta.number}</span></div>
                <div><span className="text-slate-500">Invoice Date: </span><span className="font-medium">{invoice.meta.date}</span></div>
                <div><span className="text-slate-500">Due Date: </span><span className="font-medium">{invoice.meta.dueDate}</span></div>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Bill To</div>
              <div className="font-heading font-semibold">{invoice.customer.name || "Customer name"}</div>
              <div className="text-sm text-slate-600 whitespace-pre-line">{invoice.customer.address}</div>
              {invoice.customer.email && <div className="text-sm text-slate-600 mt-1">{invoice.customer.email}</div>}
              {invoice.customer.phone && <div className="text-sm text-slate-600">{invoice.customer.phone}</div>}
            </div>
            {showQR && (
              <div className="justify-self-end">
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Scan to pay</div>
                <div className="p-2 bg-white rounded-lg border border-slate-200 inline-block">
                  <QRCodeCanvas value={qrPayload} size={96} />
                </div>
              </div>
            )}
          </div>

          {/* Items */}
          <table className="w-full text-sm mb-8">
            <thead>
              <tr className="bg-blue-600 text-white text-left">
                <th className="py-3 px-3 rounded-l-lg w-10">#</th>
                <th className="py-3 px-3">Item</th>
                <th className="py-3 px-3 w-16 text-right">Qty</th>
                <th className="py-3 px-3 w-24 text-right">Price</th>
                <th className="py-3 px-3 w-24 text-right rounded-r-lg">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => {
                const line = Number(it.qty || 0) * Number(it.price || 0);
                const total = line - (line * Number(it.discount || 0)) / 100;
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-3 px-3 text-slate-500">{i + 1}</td>
                    <td className="py-3 px-3">
                      <div className="font-medium">{it.name || "—"}</div>
                      {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                    </td>
                    <td className="py-3 px-3 text-right tabular-nums">{it.qty}</td>
                    <td className="py-3 px-3 text-right tabular-nums">{sym}{Number(it.price || 0).toFixed(2)}</td>
                    <td className="py-3 px-3 text-right tabular-nums font-medium">{sym}{total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-full sm:w-72 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="tabular-nums">{sym}{totals.subtotal.toFixed(2)}</span></div>
              {totals.discount > 0 && <div className="flex justify-between"><span className="text-slate-500">Discount ({invoice.discountPct}%)</span><span className="tabular-nums">-{sym}{totals.discount.toFixed(2)}</span></div>}
              {totals.tax > 0 && <div className="flex justify-between"><span className="text-slate-500">Tax ({invoice.taxPct}%)</span><span className="tabular-nums">{sym}{totals.tax.toFixed(2)}</span></div>}
              {totals.shipping > 0 && <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="tabular-nums">{sym}{totals.shipping.toFixed(2)}</span></div>}
              <div className="flex justify-between pt-3 border-t-2 border-slate-900">
                <span className="font-heading font-bold">Total</span>
                <span className="font-heading font-bold text-xl text-blue-600 tabular-nums">{sym}{totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment methods */}
          {activePayments.length > 0 && (
            <div className="mb-8">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Payment Methods</div>
              <div className="flex flex-wrap gap-2">
                {activePayments.map((p) => (
                  <span key={p} className="text-xs px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 capitalize">{p}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes & signature */}
          <div className="grid grid-cols-2 gap-8 pt-6 border-t border-slate-200">
            <div>
              {invoice.notes && (
                <>
                  <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Notes</div>
                  <div className="text-sm text-slate-700 whitespace-pre-line">{invoice.notes}</div>
                </>
              )}
              {invoice.terms && (
                <div className="mt-4">
                  <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Terms</div>
                  <div className="text-xs text-slate-600 whitespace-pre-line">{invoice.terms}</div>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Authorised Signature</div>
              {invoice.signature ? (
                <img src={invoice.signature} alt="signature" className="ml-auto h-16 object-contain" />
              ) : (
                <div className="h-16 border-b-2 border-slate-300 w-40 ml-auto" />
              )}
              <div className="text-xs text-slate-500 mt-1">{invoice.company.name}</div>
            </div>
          </div>

          {invoiceSettings?.show_footer_branding !== false && (
            <div className="mt-10 pt-4 border-t border-slate-100 text-center text-xs text-slate-400">
              {invoiceSettings?.footer_branding_text || "Thank you for your business · Generated with Simple Invoice"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
